import type {
  Address,
  AdminResetResult,
  AuthResponse,
  Branch,
  ConnectionTestResult,
  CounterRegistration,
  CustomerDetail,
  CustomerPage,
  DeleteAccountResult,
  DeliveryHandover,
  DeliveryQuote,
  DriverDocumentType,
  DriverJob,
  DriverStatus,
  DriverView,
  ForgotPasswordResponse,
  JobApplication,
  LoyaltyActionResult,
  LoyaltyCard,
  LoyaltyLookup,
  Membership,
  MembershipPlan,
  MenuAdminCategory,
  MenuAdminProduct,
  MenuResponse,
  Order,
  PaymentConfig,
  PaymentResult,
  PlaceOrderResponse,
  Quote,
  QuoteSendResult,
  SaveBranchRequest,
  SaveQuoteRequest,
  SaveVacancyRequest,
  ServiceAreaResponse,
  SettingsResponse,
  StaffBoard,
  StaffMember,
  StaffOrder,
  SubscribeResult,
  Vacancy,
  VehicleType,
  WebhookRegistrationResult,
} from './types';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

const ACCESS_TOKEN_KEY = 'es.accessToken';
const REFRESH_TOKEN_KEY = 'es.refreshToken';

/** A failed request, carrying the API's problem details for the UI to show. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  save(auth: AuthResponse) {
    localStorage.setItem(ACCESS_TOKEN_KEY, auth.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, auth.refreshToken);
  },
  clear() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

/** Called when the session cannot be recovered, so the app can sign the user out. */
let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: () => void) {
  onSessionExpired = handler;
}

async function parseError(response: Response): Promise<ApiError> {
  let detail = `Request failed (${response.status})`;
  let fieldErrors: Record<string, string[]> | undefined;

  try {
    const problem = await response.json();
    detail = problem.detail || problem.title || detail;
    fieldErrors = problem.errors;
  } catch {
    // A non-JSON body (a proxy error page, say) leaves the default message.
  }

  return new ApiError(response.status, detail, fieldErrors);
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
  /** Internal: stops a refresh failure from retrying forever. */
  retrying?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = false, retrying = false } = options;

  const headers: Record<string, string> = {};

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (auth) {
    const token = tokenStore.access;

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // An expired access token is refreshed once, transparently, and the original
  // request replayed — the customer never sees a checkout bounce to the login.
  if (response.status === 401 && auth && !retrying) {
    const refreshed = await tryRefresh();

    if (refreshed) {
      return request<T>(path, { ...options, retrying: true });
    }

    tokenStore.clear();
    onSessionExpired?.();
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Multipart upload. Deliberately not folded into request(): FormData must be
 * sent without an explicit Content-Type so the browser can add the multipart
 * boundary, and a JSON.stringify of a File silently produces "{}".
 */
async function upload<T>(path: string, form: FormData, retrying = false): Promise<T> {
  const headers: Record<string, string> = {};
  const token = tokenStore.access;

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, { method: 'POST', headers, body: form });

  if (response.status === 401 && !retrying) {
    const refreshed = await tryRefresh();

    if (refreshed) {
      return upload<T>(path, form, true);
    }

    tokenStore.clear();
    onSessionExpired?.();
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

/**
 * Fetches a protected file and returns a blob URL. Driver documents and
 * delivery photos need an Authorization header, so they cannot simply be put
 * in an <img src>.
 */
export async function fetchProtectedImage(path: string): Promise<string> {
  const token = tokenStore.access;

  const response = await fetch(`${BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  return URL.createObjectURL(await response.blob());
}

async function tryRefresh(): Promise<boolean> {
  const refreshToken = tokenStore.refresh;

  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      return false;
    }

    tokenStore.save((await response.json()) as AuthResponse);

    return true;
  } catch {
    return false;
  }
}

export interface CartLinePayload {
  productVariantId: string;
  quantity: number;
  addOnIds: string[];
  notes: string | null;
}

export interface DeliveryAddressPayload {
  recipientName: string;
  phone: string;
  line1: string;
  line2: string | null;
  suburb: string;
  city: string;
  postalCode: string | null;
  instructions: string | null;
  saveToProfile: boolean;
  saveLabel: string | null;
}

export const api = {
  menu: () => request<MenuResponse>('/api/menu'),

  serviceArea: () => request<ServiceAreaResponse>('/api/delivery/areas'),

  /** Prices a delivery to one address. The server geocodes it; we never send a distance. */
  quoteAddress: (address: string, suburb: string | null, subtotal: number) =>
    request<DeliveryQuote>('/api/delivery/quote-address', {
      method: 'POST',
      body: { address, suburb, subtotal },
    }),

  quote: (suburb: string, subtotal: number) =>
    request<DeliveryQuote>('/api/delivery/quote', { method: 'POST', body: { suburb, subtotal } }),

  register: (body: {
    email: string;
    password: string;
    fullName: string;
    phone: string;
    marketingOptIn: boolean;
    notifyBySms: boolean;
  }) => request<AuthResponse>('/api/auth/register', { method: 'POST', body }),

  login: (email: string, password: string) =>
    request<AuthResponse>('/api/auth/login', { method: 'POST', body: { email, password } }),

  logout: (refreshToken: string) =>
    request<void>('/api/auth/logout', { method: 'POST', body: { refreshToken } }),

  me: () => request<AuthResponse['customer']>('/api/auth/me', { auth: true }),

  forgotPassword: (email: string) =>
    request<ForgotPasswordResponse>('/api/auth/forgot-password', { method: 'POST', body: { email } }),

  resetPassword: (token: string, newPassword: string) =>
    request<AuthResponse>('/api/auth/reset-password', { method: 'POST', body: { token, newPassword } }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/api/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
      auth: true,
    }),

  addresses: () => request<Address[]>('/api/auth/addresses', { auth: true }),

  deleteAddress: (id: string) =>
    request<void>(`/api/auth/addresses/${id}`, { method: 'DELETE', auth: true }),

  placeOrder: (body: {
    fulfilment: number;
    items: CartLinePayload[];
    deliveryAddress: DeliveryAddressPayload | null;
    notes: string | null;
    useLoyaltyReward: boolean;
    /** Which shop is making it. Omitted, the API uses the default branch. */
    branchId?: string | null;
  }) => request<PlaceOrderResponse>('/api/orders', { method: 'POST', body, auth: true }),

  myOrders: () => request<Order[]>('/api/orders', { auth: true }),

  order: (id: string) => request<Order>(`/api/orders/${id}`, { auth: true }),

  paymentConfig: () => request<PaymentConfig>('/api/payments/config'),

  verifyPayment: (reference: string) =>
    request<PaymentResult>(`/api/payments/verify/${encodeURIComponent(reference)}`, { auth: true }),

  // ── loyalty ──────────────────────────────────────────────────────────────

  loyaltyCard: () => request<LoyaltyCard>('/api/loyalty', { auth: true }),

  // ── staff ────────────────────────────────────────────────────────────────

  /**
   * The kitchen board. branchId is a request, not a guarantee: staff who belong
   * to a shop always get theirs, whatever is asked for here.
   */
  staffBoard: (includeCompleted = false, branchId?: string | null) =>
    request<StaffBoard>(
      `/api/staff/board?includeCompleted=${includeCompleted}` +
        (branchId ? `&branchId=${branchId}` : ''),
      { auth: true },
    ),

  /** Puts a staff member at a shop, or at head office when branchId is null. */
  staffSetBranch: (customerId: string, branchId: string | null) =>
    request<StaffMember>('/api/staff/team/branch', {
      method: 'POST',
      body: { customerId, branchId },
      auth: true,
    }),

  staffAdvance: (orderId: string, status: string) =>
    request<StaffOrder>(`/api/staff/orders/${orderId}/advance`, {
      method: 'POST',
      body: { status },
      auth: true,
    }),

  /**
   * Signs a walk-in up for the stamp card. Email is optional — most people at a
   * till give a phone number and nothing else.
   */
  staffLoyaltyRegister: (body: {
    fullName: string;
    phone: string;
    email?: string | null;
    marketingOptIn?: boolean;
  }) => request<CounterRegistration>('/api/staff/loyalty/register', { method: 'POST', body, auth: true }),

  staffLoyaltySearch: (term: string) =>
    request<LoyaltyLookup[]>(`/api/staff/loyalty/search?term=${encodeURIComponent(term)}`, { auth: true }),

  staffAddStamp: (customerId: string, note: string | null) =>
    request<LoyaltyActionResult>('/api/staff/loyalty/stamp', {
      method: 'POST',
      body: { customerId, note },
      auth: true,
    }),

  staffTeam: () => request<StaffMember[]>('/api/staff/team', { auth: true }),

  staffSetRole: (customerId: string, role: string) =>
    request<StaffMember>('/api/staff/team/role', { method: 'POST', body: { customerId, role }, auth: true }),

  staffResetPassword: (customerId: string) =>
    request<AdminResetResult>('/api/staff/team/reset-password', {
      method: 'POST',
      body: { customerId },
      auth: true,
    }),

  staffSettings: () => request<SettingsResponse>('/api/staff/settings', { auth: true }),

  staffUpdateSettings: (values: Record<string, string>) =>
    request<SettingsResponse>('/api/staff/settings', { method: 'PUT', body: { values }, auth: true }),

  staffTestPayments: () =>
    request<ConnectionTestResult>('/api/staff/settings/test-payments', { method: 'POST', auth: true }),

  staffRegisterWebhook: (url: string) =>
    request<WebhookRegistrationResult>('/api/staff/settings/register-webhook', {
      method: 'POST',
      body: { url },
      auth: true,
    }),

  staffTestSms: (toNumber: string) =>
    request<ConnectionTestResult>('/api/staff/settings/test-sms', {
      method: 'POST',
      body: { toNumber },
      auth: true,
    }),

  staffTestEmail: (toEmail: string) =>
    request<ConnectionTestResult>('/api/staff/settings/test-email', {
      method: 'POST',
      body: { toEmail },
      auth: true,
    }),

  staffRedeem: (customerId: string, note: string | null) =>
    request<LoyaltyActionResult>('/api/staff/loyalty/redeem', {
      method: 'POST',
      body: { customerId, note },
      auth: true,
    }),

  // ---------------------------------------------------------------- drivers

  // ------------------------------------------------------- memberships

  membershipPlans: () => request<MembershipPlan[]>('/api/subscriptions/plans'),

  membershipMine: () => request<Membership | undefined>('/api/subscriptions/me', { auth: true }),

  membershipSubscribe: (planCode: string) =>
    request<SubscribeResult>('/api/subscriptions/subscribe', {
      method: 'POST',
      body: { planCode },
      auth: true,
    }),

  membershipCancel: (reason: string) =>
    request<void>('/api/subscriptions/me/cancel', { method: 'POST', body: { reason }, auth: true }),

  membershipLookup: (term: string) =>
    request<Membership[]>(`/api/subscriptions/lookup?term=${encodeURIComponent(term)}`, { auth: true }),

  membershipRedeem: (subscriptionId: string, itemName: string) =>
    request<Membership>(`/api/subscriptions/${subscriptionId}/redeem`, {
      method: 'POST',
      body: { itemName },
      auth: true,
    }),

  // -------------------------------------------------------- menu admin

  staffMenu: () => request<MenuAdminCategory[]>('/api/staff/menu', { auth: true }),

  staffSetProductAvailability: (productId: string, available: boolean) =>
    request<void>(`/api/staff/menu/products/${productId}`, {
      method: 'POST',
      body: { available },
      auth: true,
    }),

  staffSetVariantAvailability: (variantId: string, available: boolean) =>
    request<void>(`/api/staff/menu/variants/${variantId}`, {
      method: 'POST',
      body: { available },
      auth: true,
    }),

  staffSetAddOnAvailability: (addOnId: string, available: boolean) =>
    request<void>(`/api/staff/menu/addons/${addOnId}`, {
      method: 'POST',
      body: { available },
      auth: true,
    }),

  staffRestoreMenu: () =>
    request<{ restored: number }>('/api/staff/menu/restore-all', { method: 'POST', auth: true }),

  // --------------------------------------------------------- customers

  staffCustomers: (search: string, page = 1) =>
    request<CustomerPage>(
      `/api/staff/customers?search=${encodeURIComponent(search)}&page=${page}&pageSize=25`,
      { auth: true },
    ),

  // ------------------------------------------------------- corporate quotes

  quotes: (status?: string | null) =>
    request<Quote[]>('/api/quotes' + (status ? `?status=${status}` : ''), { auth: true }),

  quoteCreate: (body: SaveQuoteRequest) =>
    request<Quote>('/api/quotes', { method: 'POST', body, auth: true }),

  quoteUpdate: (id: string, body: SaveQuoteRequest) =>
    request<Quote>(`/api/quotes/${id}`, { method: 'PUT', body, auth: true }),

  quoteSend: (id: string) =>
    request<QuoteSendResult>(`/api/quotes/${id}/send`, { method: 'POST', auth: true }),

  /** Chase now. Reminders also go automatically — see QuoteService. */
  quoteRemind: (id: string) =>
    request<QuoteSendResult>(`/api/quotes/${id}/remind`, { method: 'POST', auth: true }),

  quoteMarkPaid: (id: string, paymentReference: string | null) =>
    request<Quote>(`/api/quotes/${id}/paid`, { method: 'POST', body: { paymentReference }, auth: true }),

  quoteMarkFulfilled: (id: string) =>
    request<Quote>(`/api/quotes/${id}/fulfilled`, { method: 'POST', auth: true }),

  quoteDecline: (id: string) =>
    request<Quote>(`/api/quotes/${id}/decline`, { method: 'POST', auth: true }),

  // ------------------------------------------------------------- menu admin

  staffEditProduct: (productId: string, body: { name: string; description: string; sortOrder: number }) =>
    request<MenuAdminProduct>(`/api/staff/menu/products/${productId}`, { method: 'PUT', body, auth: true }),

  staffEditVariant: (
    variantId: string,
    body: { label: string; sizeMl: number | null; price: number; sortOrder: number },
  ) => request<MenuAdminProduct>(`/api/staff/menu/variants/${variantId}`, { method: 'PUT', body, auth: true }),

  /** Multipart. The picture is stored by the API, not in the web root. */
  staffSetProductImage: async (productId: string, file: File) => {
    const form = new FormData();
    form.append('image', file);

    const response = await fetch(`${BASE_URL}/api/staff/menu/products/${productId}/image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tokenStore.access}` },
      body: form,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.detail || data.message || 'That picture did not upload.');

    return data as MenuAdminProduct;
  },

  staffClearProductImage: (productId: string) =>
    request<MenuAdminProduct>(`/api/staff/menu/products/${productId}/image`, { method: 'DELETE', auth: true }),

  // ------------------------------------------------------------ careers

  vacanciesAll: () => request<Vacancy[]>('/api/careers/vacancies/all', { auth: true }),

  vacancyCreate: (body: SaveVacancyRequest) =>
    request<Vacancy>('/api/careers/vacancies', { method: 'POST', body, auth: true }),

  vacancyUpdate: (id: string, body: SaveVacancyRequest) =>
    request<Vacancy>(`/api/careers/vacancies/${id}`, { method: 'PUT', body, auth: true }),

  /** No delete — the applications point at it. */
  vacancySetOpen: (id: string, isOpen: boolean) =>
    request<Vacancy>(`/api/careers/vacancies/${id}/open`, { method: 'POST', body: { isOpen }, auth: true }),

  applications: (vacancyId?: string | null) =>
    request<JobApplication[]>(
      '/api/careers/applications' + (vacancyId ? `?vacancyId=${vacancyId}` : ''),
      { auth: true },
    ),

  applicationReview: (id: string, status: string, internalNote: string | null) =>
    request<JobApplication>(`/api/careers/applications/${id}/review`, {
      method: 'POST',
      body: { status, internalNote },
      auth: true,
    }),

  /**
   * The CV. Fetched with the token and handed to the browser as a blob rather
   * than linked directly — the endpoint needs an Authorization header, so a
   * plain <a href> would just 401.
   */
  applicationCv: async (id: string, fileName: string) => {
    const response = await fetch(`${BASE_URL}/api/careers/applications/${id}/cv`, {
      headers: { Authorization: `Bearer ${tokenStore.access}` },
    });

    if (!response.ok) throw new Error('Could not open that CV.');

    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  },

  // ----------------------------------------------------------- branches

  /** The shops currently trading. Public — the picker needs it before sign-in. */
  branches: () => request<Branch[]>('/api/branches'),

  /** Every shop, closed ones included. Staff. */
  branchesAll: () => request<Branch[]>('/api/branches/all', { auth: true }),

  branchCreate: (body: SaveBranchRequest) =>
    request<Branch>('/api/branches', { method: 'POST', body, auth: true }),

  branchUpdate: (id: string, body: SaveBranchRequest) =>
    request<Branch>(`/api/branches/${id}`, { method: 'PUT', body, auth: true }),

  /** There is no delete: a branch owns the orders it took. */
  branchSetActive: (id: string, isActive: boolean) =>
    request<Branch>(`/api/branches/${id}/active`, { method: 'POST', body: { isActive }, auth: true }),

  branchSetDefault: (id: string) =>
    request<Branch>(`/api/branches/${id}/default`, { method: 'POST', auth: true }),

  staffCustomer: (id: string) =>
    request<CustomerDetail>(`/api/staff/customers/${id}`, { auth: true }),

  /**
   * Closes an account. Super admin only, and irreversible. The address is sent
   * back so the server can refuse if it does not match the row being deleted —
   * a mis-click in a list must not be able to erase somebody.
   */
  staffDeleteCustomer: (id: string, confirmEmail: string) =>
    request<DeleteAccountResult>(`/api/staff/customers/${id}`, {
      method: 'DELETE',
      body: { confirmEmail },
      auth: true,
    }),

  driverMe: () => request<DriverView | undefined>('/api/drivers/me', { auth: true }),

  driverRegister: (vehicle: VehicleType, vehicleRegistration: string | null) =>
    request<DriverView>('/api/drivers/me', {
      method: 'POST',
      body: { vehicle, vehicleRegistration },
      auth: true,
    }),

  driverUploadDocument: (type: DriverDocumentType, file: File, expiresOn: string | null) => {
    const form = new FormData();
    form.append('type', type);
    form.append('file', file);
    if (expiresOn) form.append('expiresOn', expiresOn);
    return upload<DriverView>('/api/drivers/me/documents', form);
  },

  driverSubmit: () => request<DriverView>('/api/drivers/me/submit', { method: 'POST', auth: true }),

  driverSetShift: (onShift: boolean) =>
    request<DriverView>('/api/drivers/me/shift', { method: 'POST', body: { onShift }, auth: true }),

  driverJobs: (includeCompleted = false) =>
    request<DriverJob[]>(`/api/drivers/me/jobs?includeCompleted=${includeCompleted}`, { auth: true }),

  driverStartJob: (orderId: string) =>
    request<DriverJob>(`/api/drivers/me/jobs/${orderId}/start`, { method: 'POST', auth: true }),

  driverCompleteJob: (
    orderId: string,
    photo: File,
    handover: DeliveryHandover,
    note: string | null,
    coords: { latitude: number; longitude: number } | null,
  ) => {
    const form = new FormData();
    form.append('photo', photo);
    form.append('handover', handover);
    if (note) form.append('note', note);
    if (coords) {
      form.append('latitude', String(coords.latitude));
      form.append('longitude', String(coords.longitude));
    }
    form.append('capturedAt', new Date().toISOString());
    return upload<DriverJob>(`/api/drivers/me/jobs/${orderId}/complete`, form);
  },

  // staff side
  driversList: (status?: DriverStatus[]) => {
    const query = status?.length ? `?${status.map((s) => `status=${s}`).join('&')}` : '';
    return request<DriverView[]>(`/api/drivers${query}`, { auth: true });
  },

  driversAvailable: () => request<DriverView[]>('/api/drivers/available', { auth: true }),

  driverApprove: (id: string) =>
    request<DriverView>(`/api/drivers/${id}/approve`, { method: 'POST', auth: true }),

  driverReject: (id: string, reason: string) =>
    request<DriverView>(`/api/drivers/${id}/reject`, { method: 'POST', body: { reason }, auth: true }),

  driverSuspend: (id: string, reason: string) =>
    request<DriverView>(`/api/drivers/${id}/suspend`, { method: 'POST', body: { reason }, auth: true }),

  driverReinstate: (id: string) =>
    request<DriverView>(`/api/drivers/${id}/reinstate`, { method: 'POST', auth: true }),

  assignDriver: (orderId: string, driverProfileId: string) =>
    request<void>(`/api/staff/orders/${orderId}/driver`, {
      method: 'POST',
      body: { driverProfileId },
      auth: true,
    }),

  unassignDriver: (orderId: string) =>
    request<void>(`/api/staff/orders/${orderId}/driver`, { method: 'DELETE', auth: true }),
};
